import { Dialog, DialogPanel } from '@headlessui/react';
import { IoMdClose } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { commonToast } from '../../helper/Toast';
import Loading from '../Loading';
import Select from 'react-select';
import {
    createStudent,
    DEFAULT_ADMIN_PASSWORD,
    getApiError,
    mapUiLanguage,
} from '../../helper/adminUsersApi';

const customSelectStyles = {
    control: (provided, state) => ({
        ...provided, minHeight: '45px', borderRadius: '0.5rem',
        borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(59,130,246,0.2)' : 'inset 0 1px 2px rgba(0,0,0,0.1)'
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 })
};

export default function AddStudentModel({ open, setOpen, onCreated, schoolId }) {
    const { t, i18n } = useTranslation();
    const firstNameRef = useRef(null);

    const initialValues = {
            firstName: '', lastName: '', email: '', password: DEFAULT_ADMIN_PASSWORD,
        gender: 'UNSPECIFIED',
        birthDate: '', nickname: '',
    };

    const validationSchema = Yup.object({
        email: Yup.string().email(t('validation.email_invalid')).required(t('validation.email_required')),
        firstName: Yup.string().required(t('validation.name_required')),
        lastName: Yup.string().required(t('validation.lastname_required')),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setSubmitting(true);
            const studentPayload = {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                email: values.email.trim(),
                password: values.password || DEFAULT_ADMIN_PASSWORD,
                preferredLanguage: mapUiLanguage(i18n.language),
                countryCode: 'LT',
                gender: values.gender || 'UNSPECIFIED',
            };
            if (schoolId) studentPayload.schoolId = schoolId;
            if (values.birthDate) studentPayload.birthDate = values.birthDate;
            if (values.nickname) studentPayload.nickname = values.nickname.trim();

            await createStudent(studentPayload);
            commonToast(t('student_added_successfully') || 'Student added successfully!', 'success');
            onCreated();
            resetForm();
            setOpen(false);
        } catch (error) {
            console.error('Error details:', error);
            commonToast(getApiError(error, 'Failed to add student'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

  

    const genderOptions = [
        { value: 'MALE', label: t('male') },
        { value: 'FEMALE', label: t('female') },
        { value: 'UNSPECIFIED', label: t('unspecified') },
    ];

    return (
        <Dialog open={open} as="div" className="relative z-40 focus:outline-none" initialFocus={firstNameRef} onClose={() => setOpen(false)}>
            <div className="fixed inset-0 bg-black/40" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel transition className="w-full max-w-md rounded-2xl bg-white p-6 duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0">
                    {/* <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}> */}
                        <Formik 
                            key={open ? 'open' : 'closed'} 
                            initialValues={initialValues} 
                            validationSchema={validationSchema} 
                            onSubmit={handleSubmit}
                            enableReinitialize={false}
                        >
                            {({ isSubmitting, setFieldValue, values }) => (
                                <Form>
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">{t('add_student_title') || "Add New Student"}</h3>
                                        <IoMdClose className="cursor-pointer text-gray-500 hover:text-gray-800" fontSize={24} onClick={() => setOpen(false)} />
                                    </div>

                                    <div className="grid grid-cols-1 gap-y-3">
                                        <InputField label={t('first_name')} name="firstName" innerRef={firstNameRef} />
                                        <InputField label={t('last_name')} name="lastName" />
                                        <InputField label={t('email')} name="email" type="email" />
                                        <SelectField label={t('gender')} options={genderOptions} value={genderOptions.find(o => o.value === values.gender)} onChange={option => setFieldValue('gender', option.value)} />
                                        {/* <InputField label={t('preferred_language')} name="preferredLanguage" />
                                        <InputField label={t('country_code')} name="countryCode" /> */}
                                        <InputField label={t('birth_date')} name="birthDate" type="date" />
                                        <InputField label={t('nickname')} name="nickname" />
                                    </div>

                                    <div className="mt-6 flex justify-end gap-4">
                                        <button type="button" className="secondary-btn" onClick={() => setOpen(false)}>{t('cancel')}</button>
                                        <button type="submit" className="primary-btn" disabled={isSubmitting}>
                                            {isSubmitting ? <Loading size={5} /> : (t('add') || 'Add')}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

const InputField = ({ label, name, type = 'text', innerRef }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <Field 
            type={type} 
            name={name} 
            innerRef={innerRef} 
            autoComplete="off"
            className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200" 
        />
        <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
    </div>
);

const SelectField = ({ label, options, value, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <Select options={options} value={value} onChange={onChange} styles={customSelectStyles} />
    </div>
); 